import {
  Box,
  Button,
  Center,
  Flex,
  Grid,
  GridItem,
  Image,
  Skeleton,
  Spinner,
  Text,
} from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import {
  Link as RouteLink,
  Navigate,
  useSearchParams,
} from "react-router-dom";
import { useLandingV2Logic } from "../Logic/useLandingV2Logic";
import { guestResumePath } from "../../Helper/guestStay";

const LandingV2Content = () => {
  const { bID, manager, loading, error, currentUser, bookingDetails, handleGetStarted, isLinkExpired } = useLandingV2Logic();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Auto-start mode is set by the QR lookup flow (LookupDrawer's "Yes,
  // that's me" handoff appends `&autoStart=1`). When set, we skip the
  // welcome card and fire `handleGetStarted` automatically the moment the
  // existing fetch/auth pipeline finishes — same code path the button
  // uses, no fork in the architecture.
  const [searchParams] = useSearchParams();
  const autoStart = searchParams.get("autoStart") === "1";
  const autoStartedRef = useRef(false);

  const isReady =
    !loading &&
    !error &&
    !!manager.name &&
    !!manager.email &&
    !!bookingDetails &&
    currentUser.status === "succeeded";

  useEffect(() => {
    if (!autoStart || autoStartedRef.current || !isReady) return;
    autoStartedRef.current = true;
    sessionStorage.setItem("isV2", "true");
    handleGetStarted();
  }, [autoStart, isReady, handleGetStarted]);

  // Preload critical image for better LCP
  React.useEffect(() => {
    if (manager.images) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = manager.images;
      document.head.appendChild(link);
    }
  }, [manager.images]);

  // Handle image loading
  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(true); // Show skeleton instead
  };

  // Preload image to ensure it's fully loaded
  React.useEffect(() => {
    if (manager.images && !imageLoaded && !imageError) {
      const img = new window.Image();
      img.onload = handleImageLoad;
      img.onerror = handleImageError;
      img.src = manager.images;
    }
  }, [manager.images, imageLoaded, imageError]);

  // No booking in the URL: resume the one this browser has open, if the
  // stay hasn't ended yet. Otherwise it's a broken link.
  if (!bID) {
    const resumePath = guestResumePath();
    if (resumePath !== "/go") {
      return <Navigate to={resumePath} replace />;
    }
    return (
      <Center h="100vh">
        <Text>Invalid link. Missing booking ID.</Text>
      </Center>
    );
  }

  // Show error state
  if (error) {
    return (
      <Center h="100vh" flexDirection="column">
        <Text fontSize="xl" mb={4}>Booking Not Found</Text>
        <Text color="gray.500" textAlign="center" px={8}>
          The booking link you're trying to access is invalid or has expired. 
          Please contact your host for assistance.
        </Text>
      </Center>
    );
  }

  // Show link expired state
  if (isLinkExpired) {
    return (
      <Center h="100vh" flexDirection="column" px={8}>
        <Text fontSize="2xl" fontWeight="bold" mb={4} className="font-gilroy-bold">
          Link Has Expired
        </Text>
        <Text color="gray.500" textAlign="center" fontSize="lg" mb={2}>
          This booking link is no longer active.
        </Text>
        <Text color="gray.400" textAlign="center" fontSize="md">
          Please contact your host to request a new link if you need access.
        </Text>
      </Center>
    );
  }

  // Auto-start path (from QR lookup confirmation). Don't render the welcome
  // card at all — the user already saw their property name + image and
  // confirmed on the previous screen. Just show a quiet loading state until
  // the auto-effect above navigates onward.
  if (autoStart) {
    return (
      <Center h="100vh" flexDirection="column">
        <Spinner size="lg" color="#817EFF" thickness="3px" />
        <Text mt={4} color="gray.400" fontSize="14px">
          Loading your booking…
        </Text>
      </Center>
    );
  }

  return (
    <Box
      minH="100vh"
      position="relative"
      // overflow: hidden would break the sticky CTA on mobile; it's only
      // needed to contain the decorative glow on desktop.
      overflow={{ base: "visible", lg: "hidden" }}
      sx={{ "@supports (height: 100dvh)": { minHeight: "100dvh" } }}
    >
      {/* Soft brand glow so large desktop viewports don't feel empty */}
      <Box
        display={{ base: "none", lg: "block" }}
        position="absolute"
        inset={0}
        pointerEvents="none"
        background="radial-gradient(760px circle at 68% 42%, rgba(129, 126, 255, 0.16), transparent 65%)"
      />

      {/* On mobile the grid is sized to the *visible* viewport (100dvh —
          excludes the browser's URL/bookmark bars) with the hero content
          centered between two flexible spacer rows and the CTA pinned to
          the bottom edge, so everything fits in a single frame. */}
      <Grid
        position="relative"
        minH="100vh"
        sx={{ "@supports (height: 100dvh)": { minHeight: "100dvh" } }}
        maxW="1160px"
        mx="auto"
        px={{ base: 8, lg: 16 }}
        alignContent={{ base: "stretch", lg: "center" }}
        templateAreas={{
          base: `"." "head" "image" "tagline" "." "cta"`,
          lg: `"head image" "tagline image" "cta image"`,
        }}
        templateRows={{
          base: "1fr auto auto auto 1fr auto",
          lg: "auto auto auto",
        }}
        templateColumns={{ base: "1fr", lg: "minmax(0, 1fr) auto" }}
        columnGap={{ lg: 20 }}
      >
        <GridItem area="head" alignSelf={{ lg: "end" }}>
          <Text className="font-gilroy-bold" fontSize={{ lg: "18px" }}>
            Welcome to
          </Text>

          {loading ? (
            <Skeleton
              w={{ base: "200px", lg: "320px" }}
              h={"50px"}
              borderRadius={"10px"}
            />
          ) : (
            <Text
              className="font-gilroy-bold"
              fontSize={{ base: "32px", lg: "46px" }}
              lineHeight={{ lg: "1.15" }}
            >
              {manager.name ? manager.name : "Custom Branding"}
            </Text>
          )}
        </GridItem>

        <GridItem
          area="image"
          justifySelf={{ base: "center", lg: "end" }}
          alignSelf="center"
          mt={{ base: 6, lg: 0 }}
        >
          {loading || !(manager.images && imageLoaded && !imageError) ? (
            <Skeleton
              w={{ base: "clamp(170px, 28vh, 250px)", md: "300px", lg: "400px" }}
              h={{ base: "clamp(170px, 28vh, 250px)", md: "300px", lg: "400px" }}
              borderRadius={{ base: "20px", lg: "28px" }}
            />
          ) : (
            <Image
              src={manager.images}
              borderRadius={{ base: "20px", lg: "28px" }}
              w={{ base: "clamp(170px, 28vh, 250px)", md: "300px", lg: "400px" }}
              h={{ base: "clamp(170px, 28vh, 250px)", md: "300px", lg: "400px" }}
              objectFit={"cover"}
              loading="eager"
              priority="high"
              onLoad={handleImageLoad}
              onError={handleImageError}
              boxShadow={{ lg: "0 30px 80px rgba(129, 126, 255, 0.25)" }}
            />
          )}
        </GridItem>

        <GridItem area="tagline" mt={{ base: 5, lg: 6 }}>
          <Flex
            flexDirection="column"
            alignItems={{ base: "center", lg: "flex-start" }}
          >
            <Text
              className="font-gilroy-bold"
              fontSize={{ base: "24px", lg: "26px" }}
            >
              Welcome to your stay
            </Text>
            <Text
              mt={2}
              textAlign={{ base: "center", lg: "left" }}
              color={"#DFDFDF"}
              fontWeight={500}
              fontSize={{ base: "16px", lg: "17px" }}
              maxW={{ lg: "400px" }}
            >
              This handy web app will support you throughout your stay
            </Text>
          </Flex>
        </GridItem>

        {/* CTA sticks to the bottom of the visible frame on mobile, with a
            fade backdrop so content never shows through behind it. */}
        <GridItem
          area="cta"
          mt={{ base: 4, lg: 10 }}
          position={{ base: "sticky", lg: "static" }}
          bottom={0}
          zIndex={1}
          bg={{
            base: "linear-gradient(180deg, rgba(26, 32, 44, 0) 0%, #1A202C 35%)",
            lg: "none",
          }}
          // Pull the CTA row out of the grid gutter (32px) so the button
          // spans wider than the content, with its own 16px side padding.
          mx={{ base: -8, lg: 0 }}
          px={{ base: 4, lg: 0 }}
          pt={{ base: 3, lg: 0 }}
          pb={{ base: "max(12px, env(safe-area-inset-bottom))", lg: 0 }}
        >
          <Button
            color="white"
            bg={"#817EFF"}
            _hover={{ transform: "translateY(-1px)" }}
            _active={{ transform: "scale(0.97)" }}
            w={{ base: "full", lg: "auto" }}
            minW={{ lg: "280px" }}
            py={6}
            px={{ base: 6, lg: 12 }}
            borderRadius={"10px"}
            // Same lively gradient + glow treatment as the check-in
            // instructions button.
            sx={{
              backgroundImage:
                "linear-gradient(270deg,#8B5CF6,#7C3AED,#6366F1,#A855F7,#8B5CF6)",
              backgroundSize: "300% 300%",
              animation:
                "ctaGradient 4s ease infinite, ctaGlow 2.5s ease-in-out infinite",
              "@keyframes ctaGradient": {
                "0%": { backgroundPosition: "0% 50%" },
                "50%": { backgroundPosition: "100% 50%" },
                "100%": { backgroundPosition: "0% 50%" },
              },
              "@keyframes ctaGlow": {
                "0%, 100%": { boxShadow: "0 0 0 0 rgba(139,92,246,0)" },
                "50%": { boxShadow: "0 0 18px 4px rgba(139,92,246,0.4)" },
              },
              transition: "transform 0.15s ease",
            }}
            isLoading={loading || !manager.name || !manager.email || !bookingDetails || currentUser.status !== "succeeded"}
            loadingText="Loading"
            isDisabled={error || isLinkExpired}
            _disabled={{
              opacity: 0.6,
              cursor: "not-allowed",
              bg: "#817EFF",
              pointerEvents: "none"
            }}
            onClick={() => {
              sessionStorage.setItem('isV2', 'true');
              handleGetStarted();
            }}
          >
            Get Started
            <Text
              as="span"
              ml="6px"
              display="inline-flex"
              alignItems="center"
              sx={{
                animation: "ctaNudge 1.2s ease-in-out infinite",
                "@keyframes ctaNudge": {
                  "0%, 100%": { transform: "translateX(0)" },
                  "50%": { transform: "translateX(4px)" },
                },
              }}
            >
              ›
            </Text>
          </Button>
        </GridItem>
      </Grid>
    </Box>
  );
};


const LandingV2 = () => {
  const [searchParams] = useSearchParams();
  const bID = searchParams.get("bID");
  const upsellID = searchParams.get("upsellID");

  // Bounce upsell deep-links before the landing bootstrap runs so we
  // don't double-fetch booking/auth on the way to /upsell.
  if (bID && upsellID) {
    return (
      <Navigate
        to={`/upsell?bID=${encodeURIComponent(bID)}&upsellID=${encodeURIComponent(upsellID)}`}
        replace
      />
    );
  }

  return <LandingV2Content />;
};

export default LandingV2; 